import React, { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import {
  FaDownload,
  FaPrint,
  FaRedo,
  FaPlus,
  FaTrash,
  FaFileInvoice,
  FaReceipt,
  FaShoppingBag,
  FaTools,
  FaConciergeBell,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import LogoUploader from '../components/LogoUploader';
import { drawPdfLogo } from '../utils/logoUpload';

const BILL_TYPES = [
  { v: 'invoice', l: 'Invoice', Icon: FaFileInvoice, prefix: 'INV', title: 'INVOICE' },
  { v: 'receipt', l: 'Receipt', Icon: FaReceipt, prefix: 'RCT', title: 'RECEIPT' },
  { v: 'sale', l: 'Sales Bill', Icon: FaShoppingBag, prefix: 'SAL', title: 'SALES BILL' },
  { v: 'service', l: 'Service Bill', Icon: FaConciergeBell, prefix: 'SRV', title: 'SERVICE BILL' },
  { v: 'work', l: 'Work Order', Icon: FaTools, prefix: 'WO', title: 'WORK ORDER' },
];

const TEMPLATES = [
  { id: 'classic', label: 'Classic A4', accent: '#1d4ed8', format: 'a4' },
  { id: 'modern', label: 'Modern Sidebar', accent: '#0e7490', format: 'a4' },
  { id: 'minimal', label: 'Minimal Mono', accent: '#111827', format: 'a4' },
  { id: 'banner', label: 'Bold Banner', accent: '#b91c1c', format: 'a4' },
  { id: 'thermal', label: 'Quick Slip 80mm', accent: '#15803d', format: [80, 297] },
];

const CURRENCIES = [
  { code: 'INR', sym: '₹', label: 'INR (₹)' },
  { code: 'USD', sym: '$', label: 'USD ($)' },
  { code: 'EUR', sym: '€', label: 'EUR (€)' },
  { code: 'GBP', sym: '£', label: 'GBP (£)' },
  { code: 'AED', sym: 'AED', label: 'AED (د.إ)' },
  { code: 'SGD', sym: 'S$', label: 'SGD (S$)' },
  { code: 'AUD', sym: 'A$', label: 'AUD (A$)' },
];

const TAX_MODES = [
  { v: 'none', l: 'No Tax' },
  { v: 'gst', l: 'GST (CGST+SGST split)' },
  { v: 'igst', l: 'IGST (single)' },
  { v: 'vat', l: 'VAT' },
  { v: 'sales', l: 'Sales Tax' },
  { v: 'custom', l: 'Custom Tax Label' },
];

const PAYMENT_MODES = ['Cash', 'UPI', 'Card', 'Bank Transfer', 'Cheque', 'Wallet', 'Net Banking', 'Other'];
const STATUSES = ['Paid', 'Unpaid', 'Partially Paid', 'Due', 'Refunded'];

const hexToRgbArr = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

const pad2 = (n) => String(n).padStart(2, '0');
const toDMY = (d) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
const todayDMY = () => toDMY(new Date());
const addDaysDMY = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toDMY(d);
};
const nowHM = () => {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const genBillNo = (prefix) => {
  const year = new Date().getFullYear();
  const seq = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${year}-${seq}`;
};

const formatNum = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const newItem = (description = '', hsn = '', qty = 1, unit = 'Nos', rate = 0, tax = 0, discount = 0) => ({
  id: Math.random().toString(36).slice(2, 9),
  description,
  hsn,
  qty,
  unit,
  rate,
  tax,
  discount,
});

const GeneralBillGenerator = () => {
  const [billType, setBillType] = useState('invoice');
  const [template, setTemplate] = useState('classic');
  const [currency, setCurrency] = useState('INR');
  const [taxMode, setTaxMode] = useState('gst');
  const [customTaxLabel, setCustomTaxLabel] = useState('Tax');

  const [business, setBusiness] = useState({
    name: 'Your Business Name',
    tagline: 'Your tagline or service line',
    address: '123, Business Street, City, State - 411001',
    phone: '+91 98765 43210',
    email: 'hello@yourbusiness.com',
    website: 'www.yourbusiness.com',
    gstin: '27ABCDE1234F1Z5',
    pan: 'ABCDE1234F',
    regNo: '',
  });

  const [client, setClient] = useState({
    name: 'Customer Name',
    contactPerson: '',
    address: '456, Customer Lane, City, State - 411002',
    phone: '+91 90000 11122',
    email: 'customer@example.com',
    gstin: '',
    placeOfSupply: 'Maharashtra',
  });

  const [meta, setMeta] = useState({
    billNo: genBillNo('INV'),
    issueDate: todayDMY(),
    dueDate: addDaysDMY(15),
    time: nowHM(),
    poRef: '',
    status: 'Unpaid',
    paymentMode: 'UPI',
  });

  const [items, setItems] = useState([
    newItem('Sample product or service', '998313', 2, 'Nos', 1500, 18, 0),
    newItem('Consultation charges', '998311', 1, 'Hrs', 2000, 18, 100),
  ]);

  const [charges, setCharges] = useState({
    shipping: 0,
    packing: 0,
    handling: 0,
    discountPercent: 0,
    advancePaid: 0,
    roundOff: true,
    intraState: true, // for GST split
  });

  const [bank, setBank] = useState({
    accountName: '',
    accountNo: '',
    bank: '',
    ifsc: '',
    upi: '',
  });

  const [notes, setNotes] = useState('Thank you for your business!');
  const [terms, setTerms] = useState(
    '1. Payment due within 15 days of invoice date.\n2. Goods once sold will not be taken back.\n3. Subject to local jurisdiction.'
  );

  const [logo, setLogo] = useState(null);

  const accent = TEMPLATES.find((t) => t.id === template)?.accent || '#1d4ed8';
  const typeConfig = BILL_TYPES.find((t) => t.v === billType);
  const currSym = CURRENCIES.find((c) => c.code === currency)?.sym || '₹';
  const pdfCurrSym = currency === 'INR' ? 'Rs.' : currSym;
  const formatPdfMoney = (amount, noSpace = false) => `${pdfCurrSym}${noSpace ? '' : ' '}${formatNum(amount)}`;
  const headerTitle = typeConfig?.title || 'BILL';

  const switchBillType = (t) => {
    setBillType(t);
    const cfg = BILL_TYPES.find((x) => x.v === t);
    setMeta((m) => ({ ...m, billNo: genBillNo(cfg?.prefix || 'BILL') }));
  };

  const taxLabel = useMemo(() => {
    if (taxMode === 'gst') return charges.intraState ? 'CGST + SGST' : 'IGST';
    if (taxMode === 'igst') return 'IGST';
    if (taxMode === 'vat') return 'VAT';
    if (taxMode === 'sales') return 'Sales Tax';
    if (taxMode === 'custom') return customTaxLabel || 'Tax';
    return '';
  }, [taxMode, charges.intraState, customTaxLabel]);

  // ---------- COMPUTATIONS ----------
  const computed = useMemo(() => {
    const lines = items.map((it) => {
      const qty = Number(it.qty) || 0;
      const rate = Number(it.rate) || 0;
      const disc = Number(it.discount) || 0; // amount, not %
      const taxPct = Number(it.tax) || 0;
      const base = qty * rate;
      const afterDisc = Math.max(0, base - disc);
      const taxAmt = taxMode === 'none' ? 0 : (afterDisc * taxPct) / 100;
      const total = afterDisc + taxAmt;
      return { ...it, base, afterDisc, taxAmt, total, taxPct };
    });
    const subtotal = lines.reduce((s, l) => s + l.base, 0);
    const lineDiscounts = lines.reduce((s, l) => s + (l.base - l.afterDisc), 0);
    const taxableBase = lines.reduce((s, l) => s + l.afterDisc, 0);
    const totalTax = lines.reduce((s, l) => s + l.taxAmt, 0);
    const cgst = taxMode === 'gst' && charges.intraState ? totalTax / 2 : 0;
    const sgst = taxMode === 'gst' && charges.intraState ? totalTax / 2 : 0;
    const igst = (taxMode === 'gst' && !charges.intraState) || taxMode === 'igst' ? totalTax : 0;
    const otherTax = taxMode === 'vat' || taxMode === 'sales' || taxMode === 'custom' ? totalTax : 0;

    const itemsTotal = taxableBase + totalTax;
    const ship = Number(charges.shipping) || 0;
    const pack = Number(charges.packing) || 0;
    const handle = Number(charges.handling) || 0;
    const extras = ship + pack + handle;
    const grossBeforeBillDisc = itemsTotal + extras;
    const billDisc = (grossBeforeBillDisc * (Number(charges.discountPercent) || 0)) / 100;
    const afterBillDisc = Math.max(0, grossBeforeBillDisc - billDisc);
    const advance = Number(charges.advancePaid) || 0;
    const due = Math.max(0, afterBillDisc - advance);
    const dueRounded = Math.round(due);
    return {
      lines,
      subtotal,
      lineDiscounts,
      taxableBase,
      totalTax,
      cgst,
      sgst,
      igst,
      otherTax,
      extras,
      ship,
      pack,
      handle,
      grossBeforeBillDisc,
      billDisc,
      afterBillDisc,
      advance,
      due: Math.round(due * 100) / 100,
      dueRounded,
      roundOff: dueRounded - due,
    };
  }, [items, charges, taxMode]);

  const finalAmount = charges.roundOff ? computed.dueRounded : computed.due;

  // ---------- HANDLERS ----------
  const updateBusiness = (k, v) => setBusiness((p) => ({ ...p, [k]: v }));
  const updateClient = (k, v) => setClient((p) => ({ ...p, [k]: v }));
  const updateMeta = (k, v) => setMeta((p) => ({ ...p, [k]: v }));
  const updateCharges = (k, v) =>
    setCharges((p) => ({ ...p, [k]: typeof v === 'boolean' ? v : (v === '' ? 0 : Number(v) || 0) }));
  const updateBank = (k, v) => setBank((p) => ({ ...p, [k]: v }));

  const addItem = () => setItems((p) => [...p, newItem()]);
  const removeItem = (id) => setItems((p) => p.filter((it) => it.id !== id));
  const updateItem = (id, k, v) =>
    setItems((p) =>
      p.map((it) =>
        it.id === id
          ? { ...it, [k]: ['qty', 'rate', 'tax', 'discount'].includes(k) ? (v === '' ? 0 : Number(v) || 0) : v }
          : it
      )
    );

  const handleReset = () => {
    setBillType('invoice');
    setTemplate('classic');
    setCurrency('INR');
    setTaxMode('gst');
    setBusiness({
      name: 'Your Business Name',
      tagline: 'Your tagline or service line',
      address: '123, Business Street, City, State - 411001',
      phone: '+91 98765 43210',
      email: 'hello@yourbusiness.com',
      website: 'www.yourbusiness.com',
      gstin: '27ABCDE1234F1Z5',
      pan: 'ABCDE1234F',
      regNo: '',
    });
    setClient({
      name: 'Customer Name',
      contactPerson: '',
      address: '456, Customer Lane, City, State - 411002',
      phone: '+91 90000 11122',
      email: 'customer@example.com',
      gstin: '',
      placeOfSupply: 'Maharashtra',
    });
    setMeta({
      billNo: genBillNo('INV'),
      issueDate: todayDMY(),
      dueDate: addDaysDMY(15),
      time: nowHM(),
      poRef: '',
      status: 'Unpaid',
      paymentMode: 'UPI',
    });
    setItems([
      newItem('Sample product or service', '998313', 2, 'Nos', 1500, 18, 0),
      newItem('Consultation charges', '998311', 1, 'Hrs', 2000, 18, 100),
    ]);
    setCharges({ shipping: 0, packing: 0, handling: 0, discountPercent: 0, advancePaid: 0, roundOff: true, intraState: true });
    setBank({ accountName: '', accountNo: '', bank: '', ifsc: '', upi: '' });
    setNotes('Thank you for your business!');
    setTerms('1. Payment due within 15 days of invoice date.\n2. Goods once sold will not be taken back.\n3. Subject to local jurisdiction.');
    setLogo(null);
  };

  const handlePrint = () => window.print();

  // ---------- PDF RENDERERS ----------
  const buildDoc = () => {
    const tpl = TEMPLATES.find((t) => t.id === template);
    const format = tpl?.format || 'a4';
    return new jsPDF({ orientation: 'portrait', unit: 'mm', format });
  };

  const renderClassicPDF = (doc) => {
    const accentRGB = hexToRgbArr(accent);
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    let y = 14;

    if (logo) drawPdfLogo(doc, logo, 14, y, 22, 22);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    doc.setTextColor(...accentRGB);
    doc.text(business.name, logo ? 40 : 14, y + 8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80);
    if (business.tagline) doc.text(business.tagline, logo ? 40 : 14, y + 13);
    doc.setFontSize(8);
    doc.setTextColor(60);
    doc.text(business.address || '', logo ? 40 : 14, y + 18);
    doc.text(`${business.phone || ''}  ·  ${business.email || ''}`, logo ? 40 : 14, y + 22);
    if (business.website) doc.text(business.website, logo ? 40 : 14, y + 26);

    // Right block: title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(...accentRGB);
    doc.text(headerTitle, pageW - 14, y + 8, { align: 'right' });
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60);
    doc.text(`No: ${meta.billNo}`, pageW - 14, y + 14, { align: 'right' });
    doc.text(`Date: ${meta.issueDate}`, pageW - 14, y + 19, { align: 'right' });
    if (meta.dueDate) doc.text(`Due: ${meta.dueDate}`, pageW - 14, y + 24, { align: 'right' });
    if (meta.poRef) doc.text(`Ref: ${meta.poRef}`, pageW - 14, y + 29, { align: 'right' });

    y += 34;
    doc.setDrawColor(...accentRGB);
    doc.setLineWidth(0.5);
    doc.line(14, y, pageW - 14, y);
    y += 6;

    // Business meta
    doc.setFontSize(8);
    doc.setTextColor(60);
    let leftMeta = '';
    if (business.gstin) leftMeta += `GSTIN: ${business.gstin}    `;
    if (business.pan) leftMeta += `PAN: ${business.pan}    `;
    if (business.regNo) leftMeta += `Reg: ${business.regNo}`;
    if (leftMeta) doc.text(leftMeta, 14, y);
    y += 6;

    // Bill To
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...accentRGB);
    doc.text('BILL TO', 14, y);
    doc.text('STATUS', pageW - 14, y, { align: 'right' });
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(40);
    doc.text(client.name, 14, y);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...hexToRgbArr(meta.status === 'Paid' ? '#15803d' : '#b91c1c'));
    doc.text(meta.status, pageW - 14, y, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60);
    y += 5;
    if (client.contactPerson) { doc.text(`Attn: ${client.contactPerson}`, 14, y); y += 4; }
    const caddr = doc.splitTextToSize(client.address || '', pageW / 2);
    doc.text(caddr, 14, y);
    y += caddr.length * 4;
    if (client.phone) { doc.text(`Phone: ${client.phone}`, 14, y); y += 4; }
    if (client.email) { doc.text(`Email: ${client.email}`, 14, y); y += 4; }
    if (client.gstin) { doc.text(`GSTIN: ${client.gstin}`, 14, y); y += 4; }
    if (taxMode === 'gst' && client.placeOfSupply) { doc.text(`Place of Supply: ${client.placeOfSupply}`, 14, y); y += 4; }

    y += 4;
    // Items table
    const showHsn = taxMode === 'gst' || taxMode === 'igst';
    const colX = showHsn
      ? { sn: 16, desc: 24, hsn: 80, qty: 100, rate: 120, disc: 142, tax: 160, total: pageW - 16 }
      : { sn: 16, desc: 24, qty: 110, rate: 135, disc: 158, tax: 178, total: pageW - 16 };

    doc.setFillColor(...accentRGB);
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.rect(14, y, pageW - 28, 8, 'F');
    doc.text('#', colX.sn, y + 5.5);
    doc.text('Description', colX.desc, y + 5.5);
    if (showHsn) doc.text('HSN', colX.hsn, y + 5.5);
    doc.text('Qty', colX.qty, y + 5.5, { align: 'right' });
    doc.text('Rate', colX.rate, y + 5.5, { align: 'right' });
    doc.text('Disc', colX.disc, y + 5.5, { align: 'right' });
    if (taxMode !== 'none') doc.text('Tax%', colX.tax, y + 5.5, { align: 'right' });
    doc.text('Total', colX.total, y + 5.5, { align: 'right' });
    y += 10;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40);
    computed.lines.forEach((l, idx) => {
      if (y > pageH - 60) {
        doc.addPage();
        y = 20;
      }
      if (idx % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(14, y - 4, pageW - 28, 8, 'F');
      }
      doc.text(String(idx + 1), colX.sn, y + 1);
      const descLines = doc.splitTextToSize(`${l.description}${l.unit ? ` (${l.unit})` : ''}`, (showHsn ? 50 : 80));
      doc.text(descLines, colX.desc, y + 1);
      if (showHsn) doc.text(l.hsn || '-', colX.hsn, y + 1);
      doc.text(String(l.qty), colX.qty, y + 1, { align: 'right' });
      doc.text(formatNum(l.rate), colX.rate, y + 1, { align: 'right' });
      doc.text(formatNum(l.discount), colX.disc, y + 1, { align: 'right' });
      if (taxMode !== 'none') doc.text(`${l.taxPct}%`, colX.tax, y + 1, { align: 'right' });
      doc.text(formatNum(l.total), colX.total, y + 1, { align: 'right' });
      y += Math.max(6, descLines.length * 4);
    });

    y += 4;
    doc.setDrawColor(...accentRGB);
    doc.line(pageW / 2, y, pageW - 14, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60);
    const sumRow = (label, val, sign = '') => {
      doc.text(label, pageW / 2 + 4, y);
      doc.text(`${sign}${formatPdfMoney(val)}`, pageW - 16, y, { align: 'right' });
      y += 5;
    };
    sumRow('Subtotal', computed.subtotal);
    if (computed.lineDiscounts > 0) sumRow('Line Discounts', computed.lineDiscounts, '- ');
    sumRow('Taxable Amount', computed.taxableBase);
    if (taxMode === 'gst' && charges.intraState) {
      sumRow('CGST', computed.cgst);
      sumRow('SGST', computed.sgst);
    } else if (computed.igst > 0) {
      sumRow('IGST', computed.igst);
    } else if (computed.otherTax > 0) {
      sumRow(taxLabel, computed.otherTax);
    }
    if (computed.ship > 0) sumRow('Shipping', computed.ship);
    if (computed.pack > 0) sumRow('Packing', computed.pack);
    if (computed.handle > 0) sumRow('Handling', computed.handle);
    if (computed.billDisc > 0) sumRow(`Bill Discount (${charges.discountPercent}%)`, computed.billDisc, '- ');
    if (computed.advance > 0) sumRow('Advance Paid', computed.advance, '- ');
    if (charges.roundOff) sumRow('Round Off', computed.roundOff);

    y += 2;
    doc.setFillColor(...accentRGB);
    doc.rect(pageW / 2, y, pageW / 2 - 14, 10, 'F');
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('AMOUNT DUE', pageW / 2 + 4, y + 6.5);
    doc.text(formatPdfMoney(finalAmount), pageW - 16, y + 6.5, { align: 'right' });
    y += 16;

    // Bank details
    if (bank.accountNo || bank.upi) {
      doc.setTextColor(40);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Payment Details', 14, y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      let by = y + 4;
      if (bank.accountName) { doc.text(`A/C Name: ${bank.accountName}`, 14, by); by += 4; }
      if (bank.accountNo) { doc.text(`A/C No: ${bank.accountNo}`, 14, by); by += 4; }
      if (bank.bank) { doc.text(`Bank: ${bank.bank}`, 14, by); by += 4; }
      if (bank.ifsc) { doc.text(`IFSC: ${bank.ifsc}`, 14, by); by += 4; }
      if (bank.upi) { doc.text(`UPI: ${bank.upi}`, 14, by); by += 4; }
      y = Math.max(y, by);
    }

    // Notes & Terms
    y = Math.max(y, pageH - 60);
    if (notes) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(40);
      doc.text('Notes:', 14, y);
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(80);
      const nl = doc.splitTextToSize(notes, pageW - 28);
      doc.text(nl, 14, y + 4);
      y += 4 + nl.length * 4;
    }
    if (terms) {
      y += 2;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(40);
      doc.text('Terms & Conditions:', 14, y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(80);
      const tl = doc.splitTextToSize(terms, pageW - 28);
      doc.text(tl, 14, y + 4);
    }

    // Signature
    doc.setDrawColor(120);
    doc.setLineWidth(0.3);
    doc.line(pageW - 70, pageH - 20, pageW - 16, pageH - 20);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(80);
    doc.text(`For ${business.name}`, pageW - 43, pageH - 16, { align: 'center' });
    doc.text('Authorised Signature', pageW - 43, pageH - 12, { align: 'center' });
  };

  const renderModernPDF = (doc) => {
    const accentRGB = hexToRgbArr(accent);
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    doc.setFillColor(...accentRGB);
    doc.rect(0, 0, 58, pageH, 'F');
    doc.setTextColor(255);
    if (logo) drawPdfLogo(doc, logo, 8, 10, 22, 22);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(business.name, 8, logo ? 40 : 18);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    const ba = doc.splitTextToSize(business.address || '', 46);
    doc.text(ba, 8, logo ? 46 : 24);
    let sy = (logo ? 46 : 24) + ba.length * 3 + 2;
    doc.text(business.phone || '', 8, sy); sy += 3;
    doc.text(business.email || '', 8, sy); sy += 3;
    if (business.gstin) { doc.text(`GST: ${business.gstin}`, 8, sy); sy += 3; }

    sy = Math.max(sy, 80);
    doc.setFontSize(7);
    doc.text('BILL TO', 8, sy); sy += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(client.name, 8, sy); sy += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    const ca = doc.splitTextToSize(client.address || '', 46);
    doc.text(ca, 8, sy); sy += ca.length * 3 + 1;
    if (client.phone) { doc.text(client.phone, 8, sy); sy += 3; }
    if (client.gstin) { doc.text(`GST: ${client.gstin}`, 8, sy); sy += 3; }

    // Main
    let y = 18;
    doc.setTextColor(...accentRGB);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(headerTitle, 64, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80);
    doc.text(`${meta.billNo}  ·  ${meta.issueDate}`, 64, y);
    if (meta.dueDate) { y += 4; doc.text(`Due: ${meta.dueDate}`, 64, y); }

    y += 10;
    // Items
    doc.setFillColor(...accentRGB);
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.rect(64, y, pageW - 78, 7, 'F');
    doc.text('Item', 66, y + 4.5);
    doc.text('Qty', pageW - 70, y + 4.5, { align: 'right' });
    doc.text('Rate', pageW - 50, y + 4.5, { align: 'right' });
    doc.text('Total', pageW - 16, y + 4.5, { align: 'right' });
    y += 10;

    doc.setTextColor(40);
    doc.setFont('helvetica', 'normal');
    computed.lines.forEach((l, idx) => {
      if (y > pageH - 60) { doc.addPage(); y = 20; }
      if (idx % 2 === 0) {
        doc.setFillColor(245, 247, 250);
        doc.rect(64, y - 3, pageW - 78, 6, 'F');
      }
      const dl = doc.splitTextToSize(l.description, 70);
      doc.text(dl, 66, y + 1);
      doc.text(String(l.qty), pageW - 70, y + 1, { align: 'right' });
      doc.text(formatNum(l.rate), pageW - 50, y + 1, { align: 'right' });
      doc.text(formatNum(l.total), pageW - 16, y + 1, { align: 'right' });
      y += Math.max(6, dl.length * 4);
    });

    y += 4;
    doc.setDrawColor(200);
    doc.line(pageW - 90, y, pageW - 14, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const r = (l, v) => { doc.text(l, pageW - 90, y); doc.text(formatPdfMoney(v), pageW - 14, y, { align: 'right' }); y += 4; };
    r('Subtotal', computed.taxableBase);
    if (computed.totalTax > 0) r(taxLabel, computed.totalTax);
    if (computed.extras > 0) r('Other Charges', computed.extras);
    if (computed.billDisc > 0) r('Discount', -computed.billDisc);
    if (computed.advance > 0) r('Advance', -computed.advance);

    y += 2;
    doc.setFillColor(...accentRGB);
    doc.rect(pageW - 90, y, 76, 12, 'F');
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('AMOUNT DUE', pageW - 88, y + 8);
    doc.text(formatPdfMoney(finalAmount), pageW - 16, y + 8, { align: 'right' });
  };

  const renderMinimalPDF = (doc) => {
    const pageW = doc.internal.pageSize.getWidth();
    let y = 18;
    doc.setTextColor(20);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(24);
    doc.text(headerTitle, 14, y);
    if (logo) drawPdfLogo(doc, logo, pageW - 36, y - 10, 22, 22);

    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(`${meta.billNo} · ${meta.issueDate}${meta.dueDate ? ` · Due ${meta.dueDate}` : ''}`, 14, y);

    y += 12;
    doc.setDrawColor(20); doc.setLineWidth(0.4);
    doc.line(14, y, pageW - 14, y);

    y += 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(20);
    doc.text('From', 14, y);
    doc.text('To', pageW / 2 + 4, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(50);
    doc.text(business.name, 14, y);
    doc.text(client.name, pageW / 2 + 4, y);
    y += 4;
    const fa = doc.splitTextToSize(business.address || '', pageW / 2 - 20);
    const ta = doc.splitTextToSize(client.address || '', pageW / 2 - 20);
    doc.text(fa, 14, y);
    doc.text(ta, pageW / 2 + 4, y);
    y += Math.max(fa.length, ta.length) * 4 + 2;
    doc.text(business.phone || '', 14, y);
    doc.text(client.phone || '', pageW / 2 + 4, y);

    y = Math.max(y + 12, 80);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(20);
    doc.text('Item', 14, y);
    doc.text('Qty', pageW - 90, y, { align: 'right' });
    doc.text('Rate', pageW - 60, y, { align: 'right' });
    doc.text('Total', pageW - 14, y, { align: 'right' });
    y += 2;
    doc.line(14, y, pageW - 14, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40);
    computed.lines.forEach((l) => {
      const dl = doc.splitTextToSize(l.description, 90);
      doc.text(dl, 14, y);
      doc.text(String(l.qty), pageW - 90, y, { align: 'right' });
      doc.text(formatNum(l.rate), pageW - 60, y, { align: 'right' });
      doc.text(formatNum(l.total), pageW - 14, y, { align: 'right' });
      y += Math.max(5, dl.length * 4);
    });

    y += 4;
    doc.setDrawColor(20);
    doc.line(14, y, pageW - 14, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const r = (l, v) => { doc.text(l, pageW - 70, y); doc.text(formatPdfMoney(v), pageW - 14, y, { align: 'right' }); y += 5; };
    r('Subtotal', computed.taxableBase);
    if (computed.totalTax > 0) r(taxLabel, computed.totalTax);
    if (computed.extras > 0) r('Other', computed.extras);
    if (computed.billDisc > 0) r('Discount', -computed.billDisc);
    if (computed.advance > 0) r('Advance', -computed.advance);

    y += 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Total Due', 14, y);
    doc.text(formatPdfMoney(finalAmount), pageW - 14, y, { align: 'right' });
  };

  const renderBannerPDF = (doc) => {
    const accentRGB = hexToRgbArr(accent);
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    doc.setFillColor(...accentRGB);
    doc.rect(0, 0, pageW, 46, 'F');
    doc.setTextColor(255);
    if (logo) drawPdfLogo(doc, logo, 14, 11, 24, 24);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text(headerTitle, logo ? 44 : 14, 24);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(business.name, logo ? 44 : 14, 32);
    doc.setFontSize(8);
    doc.text(business.phone || '', logo ? 44 : 14, 38);

    doc.setFontSize(9);
    doc.text(meta.billNo, pageW - 14, 18, { align: 'right' });
    doc.text(meta.issueDate, pageW - 14, 24, { align: 'right' });
    if (meta.dueDate) doc.text(`Due ${meta.dueDate}`, pageW - 14, 30, { align: 'right' });
    doc.setFont('helvetica', 'bold');
    doc.text(meta.status, pageW - 14, 36, { align: 'right' });

    let y = 56;
    doc.setTextColor(40);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Bill To', 14, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(client.name, 14, y); y += 4;
    const ca = doc.splitTextToSize(client.address || '', pageW - 28);
    doc.text(ca, 14, y); y += ca.length * 4;
    if (client.phone) { doc.text(client.phone, 14, y); y += 4; }
    if (client.gstin) { doc.text(`GSTIN: ${client.gstin}`, 14, y); y += 4; }

    y += 4;
    doc.setFillColor(...accentRGB);
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.rect(14, y, pageW - 28, 8, 'F');
    doc.text('Description', 16, y + 5.5);
    doc.text('Qty', pageW - 90, y + 5.5, { align: 'right' });
    doc.text('Rate', pageW - 60, y + 5.5, { align: 'right' });
    doc.text('Total', pageW - 16, y + 5.5, { align: 'right' });
    y += 10;
    doc.setTextColor(40);
    doc.setFont('helvetica', 'normal');
    computed.lines.forEach((l, idx) => {
      if (y > pageH - 50) { doc.addPage(); y = 20; }
      if (idx % 2 === 0) {
        doc.setFillColor(255, 247, 237);
        doc.rect(14, y - 3, pageW - 28, 6, 'F');
      }
      const dl = doc.splitTextToSize(l.description, 100);
      doc.text(dl, 16, y + 1);
      doc.text(String(l.qty), pageW - 90, y + 1, { align: 'right' });
      doc.text(formatNum(l.rate), pageW - 60, y + 1, { align: 'right' });
      doc.text(formatNum(l.total), pageW - 16, y + 1, { align: 'right' });
      y += Math.max(6, dl.length * 4);
    });

    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const r = (l, v) => { doc.text(l, pageW - 70, y); doc.text(formatPdfMoney(v), pageW - 16, y, { align: 'right' }); y += 5; };
    r('Subtotal', computed.taxableBase);
    if (computed.totalTax > 0) r(taxLabel, computed.totalTax);
    if (computed.extras > 0) r('Other', computed.extras);
    if (computed.billDisc > 0) r('Discount', -computed.billDisc);
    if (computed.advance > 0) r('Advance', -computed.advance);

    y += 2;
    doc.setFillColor(...accentRGB);
    doc.rect(14, y, pageW - 28, 14, 'F');
    doc.setTextColor(255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('AMOUNT DUE', 18, y + 9);
    doc.text(formatPdfMoney(finalAmount), pageW - 18, y + 9, { align: 'right' });
  };

  const renderThermalPDF = (doc) => {
    const accentRGB = hexToRgbArr(accent);
    const pageW = doc.internal.pageSize.getWidth();
    let y = 6;
    if (logo) { drawPdfLogo(doc, logo, pageW / 2 - 7, y, 14, 14); y += 16; }
    doc.setTextColor(...accentRGB);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(business.name || '', pageW / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(60);
    const ba = doc.splitTextToSize(business.address || '', pageW - 6);
    doc.text(ba, pageW / 2, y, { align: 'center' });
    y += ba.length * 3 + 1;
    doc.text(business.phone || '', pageW / 2, y, { align: 'center' });
    if (business.gstin) { y += 3; doc.text(`GST: ${business.gstin}`, pageW / 2, y, { align: 'center' }); }

    y += 4;
    doc.setLineDashPattern([1, 1], 0);
    doc.line(3, y, pageW - 3, y);
    doc.setLineDashPattern([], 0);

    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...accentRGB);
    doc.text(headerTitle, pageW / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(40);
    doc.text(`No: ${meta.billNo}`, 3, y); y += 3;
    doc.text(`Date: ${meta.issueDate}  ${meta.time}`, 3, y); y += 3;
    doc.text(`To: ${client.name}`, 3, y); y += 3;
    if (client.phone) { doc.text(`Ph: ${client.phone}`, 3, y); y += 3; }

    y += 1;
    doc.setLineDashPattern([1, 1], 0);
    doc.line(3, y, pageW - 3, y);
    doc.setLineDashPattern([], 0);
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.text('Item', 3, y);
    doc.text('Qty', pageW - 20, y, { align: 'right' });
    doc.text('Amt', pageW - 3, y, { align: 'right' });
    y += 3;
    doc.setFont('helvetica', 'normal');
    computed.lines.forEach((l) => {
      const dl = doc.splitTextToSize(l.description, pageW - 26);
      doc.text(dl, 3, y);
      doc.text(String(l.qty), pageW - 20, y, { align: 'right' });
      doc.text(formatNum(l.total), pageW - 3, y, { align: 'right' });
      y += Math.max(3, dl.length * 3);
    });

    y += 1;
    doc.setLineDashPattern([1, 1], 0);
    doc.line(3, y, pageW - 3, y);
    doc.setLineDashPattern([], 0);

    y += 4;
    const r = (l, v) => { doc.text(l, 3, y); doc.text(formatNum(v), pageW - 3, y, { align: 'right' }); y += 3; };
    r('Subtotal', computed.taxableBase);
    if (computed.totalTax > 0) r(taxLabel, computed.totalTax);
    if (computed.extras > 0) r('Other', computed.extras);
    if (computed.billDisc > 0) r('Disc', -computed.billDisc);
    if (computed.advance > 0) r('Advance', -computed.advance);

    y += 1;
    doc.setLineDashPattern([1, 1], 0);
    doc.line(3, y, pageW - 3, y);
    doc.setLineDashPattern([], 0);

    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...accentRGB);
    doc.text('TOTAL', 3, y);
    doc.text(formatPdfMoney(finalAmount, true), pageW - 3, y, { align: 'right' });

    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(60);
    doc.text(`Mode: ${meta.paymentMode}  ·  ${meta.status}`, pageW / 2, y, { align: 'center' });
    y += 4;
    doc.text('Thank you!', pageW / 2, y, { align: 'center' });
  };

  const TEMPLATE_RENDERERS = {
    classic: renderClassicPDF,
    modern: renderModernPDF,
    minimal: renderMinimalPDF,
    banner: renderBannerPDF,
    thermal: renderThermalPDF,
  };

  const handleDownloadPDF = () => {
    const doc = buildDoc();
    (TEMPLATE_RENDERERS[template] || renderClassicPDF)(doc);
    const safe = (meta.billNo || 'bill').replace(/[^\w-]/g, '_');
    doc.save(`${safe}.pdf`);
  };

  // ---------- PREVIEW ----------
  const Preview = () => {
    if (template === 'thermal') {
      return (
        <div style={{ background: '#fff', color: '#111', padding: 14, borderRadius: 8, maxWidth: 280, margin: '0 auto', fontFamily: 'monospace', fontSize: 12, border: `2px dashed ${accent}` }}>
          <div style={{ textAlign: 'center', color: accent, fontWeight: 700 }}>{business.name}</div>
          <div style={{ textAlign: 'center', fontSize: 10, color: '#555' }}>{business.address}</div>
          <div style={{ textAlign: 'center', fontSize: 10 }}>{business.phone}</div>
          <hr style={{ borderTop: `1px dashed ${accent}` }} />
          <div style={{ textAlign: 'center', fontWeight: 700, color: accent }}>{headerTitle}</div>
          <div style={{ fontSize: 10 }}>
            <div>No: {meta.billNo}</div>
            <div>Date: {meta.issueDate} {meta.time}</div>
            <div>To: {client.name}</div>
          </div>
          <hr style={{ borderTop: `1px dashed ${accent}` }} />
          {computed.lines.map((l) => (
            <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ flex: 1 }}>{l.description} × {l.qty}</span>
              <span>{formatNum(l.total)}</span>
            </div>
          ))}
          <hr style={{ borderTop: `1px dashed ${accent}` }} />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Subtotal</span><span>{formatNum(computed.taxableBase)}</span></div>
          {computed.totalTax > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>{taxLabel}</span><span>{formatNum(computed.totalTax)}</span></div>}
          {computed.extras > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Other</span><span>{formatNum(computed.extras)}</span></div>}
          {computed.billDisc > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Discount</span><span>-{formatNum(computed.billDisc)}</span></div>}
          <hr style={{ borderTop: `1px dashed ${accent}` }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', color: accent, fontWeight: 700 }}><span>TOTAL</span><span>{currSym}{formatNum(finalAmount)}</span></div>
          <div style={{ textAlign: 'center', fontSize: 10, marginTop: 6 }}>{meta.paymentMode} · {meta.status}</div>
          <div style={{ textAlign: 'center', fontSize: 10 }}>Thank you!</div>
        </div>
      );
    }

    const isModern = template === 'modern';
    const isMinimal = template === 'minimal';
    const isBanner = template === 'banner';

    return (
      <div style={{
        background: '#fff', color: '#111', padding: isModern ? 0 : 24, borderRadius: 10,
        maxWidth: 800, margin: '0 auto',
        border: `1px solid ${isMinimal ? '#222' : 'rgba(0,0,0,0.08)'}`,
        display: isModern ? 'flex' : 'block', overflow: 'hidden',
      }}>
        {isModern && (
          <div style={{ background: accent, color: '#fff', padding: 18, width: 200 }}>
            {logo && <img src={logo} alt="logo" style={{ width: 50, height: 50, marginBottom: 8 }} />}
            <div style={{ fontWeight: 700, fontSize: 14 }}>{business.name}</div>
            <div style={{ fontSize: 11, marginTop: 4, opacity: 0.9 }}>{business.address}</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>{business.phone}</div>
            <div style={{ fontSize: 11 }}>{business.email}</div>
            {business.gstin && <div style={{ fontSize: 10, marginTop: 6 }}>GST: {business.gstin}</div>}
            <div style={{ marginTop: 16, fontSize: 10, opacity: 0.8 }}>BILL TO</div>
            <div style={{ fontWeight: 700, fontSize: 12 }}>{client.name}</div>
            <div style={{ fontSize: 10, opacity: 0.9 }}>{client.address}</div>
            {client.phone && <div style={{ fontSize: 10 }}>{client.phone}</div>}
          </div>
        )}
        <div style={{ flex: 1, padding: isModern ? 24 : 0 }}>
          {isBanner && (
            <div style={{ background: accent, color: '#fff', margin: -24, marginBottom: 16, padding: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {logo && <img src={logo} alt="" style={{ width: 50, height: 50 }} />}
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800 }}>{headerTitle}</div>
                  <div style={{ fontSize: 12, opacity: 0.9 }}>{business.name}</div>
                </div>
              </div>
              <div style={{ textAlign: 'right', fontSize: 11 }}>
                <div>{meta.billNo}</div>
                <div>{meta.issueDate}</div>
                {meta.dueDate && <div>Due {meta.dueDate}</div>}
                <div style={{ fontWeight: 700 }}>{meta.status}</div>
              </div>
            </div>
          )}

          {!isModern && !isBanner && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 12 }}>
                {logo && <img src={logo} alt="" style={{ width: 56, height: 56 }} />}
                <div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: isMinimal ? '#111' : accent }}>{business.name}</div>
                  {business.tagline && <div style={{ fontSize: 12, color: '#666' }}>{business.tagline}</div>}
                  <div style={{ fontSize: 12, color: '#444' }}>{business.address}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>{business.phone} · {business.email}</div>
                  {business.gstin && <div style={{ fontSize: 11, color: '#666' }}>GSTIN: {business.gstin}</div>}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 800, fontSize: 22, color: isMinimal ? '#111' : accent }}>{headerTitle}</div>
                <div style={{ fontSize: 12 }}>No: {meta.billNo}</div>
                <div style={{ fontSize: 12 }}>Date: {meta.issueDate}</div>
                {meta.dueDate && <div style={{ fontSize: 12 }}>Due: {meta.dueDate}</div>}
                <div style={{ fontSize: 12, marginTop: 4, fontWeight: 700, color: meta.status === 'Paid' ? '#15803d' : '#b91c1c' }}>{meta.status}</div>
              </div>
            </div>
          )}

          {!isModern && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 12, marginBottom: 14 }}>
              <div style={{ fontWeight: 700, color: isMinimal ? '#111' : accent, marginBottom: 4 }}>Bill To</div>
              <div style={{ fontSize: 13 }}>
                <div><b>{client.name}</b></div>
                {client.contactPerson && <div>Attn: {client.contactPerson}</div>}
                <div>{client.address}</div>
                {client.phone && <div>Phone: {client.phone}</div>}
                {client.email && <div>Email: {client.email}</div>}
                {client.gstin && <div>GSTIN: {client.gstin}</div>}
              </div>
            </div>
          )}

          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginBottom: 12 }}>
            <thead>
              <tr style={{ background: accent, color: '#fff' }}>
                <th style={{ padding: 6, textAlign: 'left' }}>#</th>
                <th style={{ padding: 6, textAlign: 'left' }}>Description</th>
                <th style={{ padding: 6, textAlign: 'right' }}>Qty</th>
                <th style={{ padding: 6, textAlign: 'right' }}>Rate</th>
                {taxMode !== 'none' && <th style={{ padding: 6, textAlign: 'right' }}>Tax%</th>}
                <th style={{ padding: 6, textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {computed.lines.map((l, i) => (
                <tr key={l.id} style={{ background: i % 2 === 0 ? '#f8fafc' : '#fff' }}>
                  <td style={{ padding: 6 }}>{i + 1}</td>
                  <td style={{ padding: 6 }}>
                    {l.description}
                    {l.unit && <span style={{ color: '#888' }}> ({l.unit})</span>}
                    {l.hsn && <div style={{ fontSize: 10, color: '#888' }}>HSN: {l.hsn}</div>}
                  </td>
                  <td style={{ padding: 6, textAlign: 'right' }}>{l.qty}</td>
                  <td style={{ padding: 6, textAlign: 'right' }}>{formatNum(l.rate)}</td>
                  {taxMode !== 'none' && <td style={{ padding: 6, textAlign: 'right' }}>{l.taxPct}%</td>}
                  <td style={{ padding: 6, textAlign: 'right', fontWeight: 600 }}>{currSym} {formatNum(l.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <table style={{ fontSize: 13, minWidth: 280 }}>
              <tbody>
                <tr><td style={{ padding: '3px 12px 3px 0' }}>Subtotal</td><td style={{ textAlign: 'right' }}>{currSym} {formatNum(computed.subtotal)}</td></tr>
                {computed.lineDiscounts > 0 && <tr><td>Line Discounts</td><td style={{ textAlign: 'right' }}>- {currSym} {formatNum(computed.lineDiscounts)}</td></tr>}
                <tr><td>Taxable</td><td style={{ textAlign: 'right' }}>{currSym} {formatNum(computed.taxableBase)}</td></tr>
                {taxMode === 'gst' && charges.intraState && (<>
                  <tr><td>CGST</td><td style={{ textAlign: 'right' }}>{currSym} {formatNum(computed.cgst)}</td></tr>
                  <tr><td>SGST</td><td style={{ textAlign: 'right' }}>{currSym} {formatNum(computed.sgst)}</td></tr>
                </>)}
                {computed.igst > 0 && <tr><td>IGST</td><td style={{ textAlign: 'right' }}>{currSym} {formatNum(computed.igst)}</td></tr>}
                {computed.otherTax > 0 && <tr><td>{taxLabel}</td><td style={{ textAlign: 'right' }}>{currSym} {formatNum(computed.otherTax)}</td></tr>}
                {computed.ship > 0 && <tr><td>Shipping</td><td style={{ textAlign: 'right' }}>{currSym} {formatNum(computed.ship)}</td></tr>}
                {computed.pack > 0 && <tr><td>Packing</td><td style={{ textAlign: 'right' }}>{currSym} {formatNum(computed.pack)}</td></tr>}
                {computed.handle > 0 && <tr><td>Handling</td><td style={{ textAlign: 'right' }}>{currSym} {formatNum(computed.handle)}</td></tr>}
                {computed.billDisc > 0 && <tr><td>Bill Discount ({charges.discountPercent}%)</td><td style={{ textAlign: 'right' }}>- {currSym} {formatNum(computed.billDisc)}</td></tr>}
                {computed.advance > 0 && <tr><td>Advance Paid</td><td style={{ textAlign: 'right' }}>- {currSym} {formatNum(computed.advance)}</td></tr>}
                {charges.roundOff && <tr><td>Round Off</td><td style={{ textAlign: 'right' }}>{formatNum(computed.roundOff)}</td></tr>}
                <tr style={{ background: accent, color: '#fff', fontWeight: 700, fontSize: 15 }}>
                  <td style={{ padding: 8 }}>AMOUNT DUE</td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{currSym} {formatNum(finalAmount)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {(bank.accountNo || bank.upi) && !isModern && (
            <div style={{ marginTop: 14, fontSize: 12, color: '#444' }}>
              <b>Payment Details:</b>{' '}
              {bank.accountName && <>A/C {bank.accountName} · </>}
              {bank.accountNo && <>{bank.accountNo} · </>}
              {bank.bank && <>{bank.bank} · </>}
              {bank.ifsc && <>IFSC {bank.ifsc} · </>}
              {bank.upi && <>UPI {bank.upi}</>}
            </div>
          )}
          {notes && <div style={{ marginTop: 10, fontSize: 12, color: '#555', fontStyle: 'italic' }}>{notes}</div>}
          {terms && <div style={{ marginTop: 8, fontSize: 11, color: '#666', whiteSpace: 'pre-wrap' }}>{terms}</div>}
        </div>
      </div>
    );
  };

  // ---------- RENDER ----------
  return (
    <div className="calculator-page fade-in">
      <SEO
        title="General Bill Generator | Universal Invoice, Receipt & Sales Bill Maker"
        description="Create any type of bill - invoice, receipt, sales bill, service bill or work order - with GST/VAT/Sales Tax, multi-currency support and 5 professional PDF templates."
        keywords="general bill generator, invoice generator, receipt maker, sales bill, service bill, GST invoice, VAT invoice, multi currency bill"
        canonicalPath="/general-bill"
        calculatorType="GeneralBillGenerator"
        calculatorDescription="Universal bill maker supporting invoices, receipts, sales/service bills and work orders with GST/VAT/Sales tax and printable PDF templates."
      />

      <div className="calculator-header">
        <h1>General Bill Generator</h1>
        <p>Universal bill maker - invoices, receipts, sales/service bills - with GST, VAT, multi-currency and 5 templates.</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          {/* Bill type */}
          <div className="input-group">
            <label>Bill Type</label>
            <div className="flex-row" style={{ flexWrap: 'wrap', gap: 8 }}>
              {BILL_TYPES.map((t) => {
                const Icon = t.Icon;
                const active = billType === t.v;
                return (
                  <button
                    key={t.v}
                    type="button"
                    onClick={() => switchBillType(t.v)}
                    className={active ? 'btn btn-primary' : 'btn btn-outline'}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <Icon /> {t.l}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Template */}
          <div className="input-group">
            <label>Template</label>
            <div className="flex-row" style={{ flexWrap: 'wrap', gap: 8 }}>
              {TEMPLATES.map((t) => {
                const active = template === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplate(t.id)}
                    className={active ? 'btn btn-primary' : 'btn btn-outline'}
                    style={{ borderColor: active ? t.accent : undefined, background: active ? t.accent : undefined }}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Currency & Tax */}
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Currency</label>
              <div className="input-with-icon">
                <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
                  {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Tax Mode</label>
              <div className="input-with-icon">
                <select value={taxMode} onChange={(e) => setTaxMode(e.target.value)}>
                  {TAX_MODES.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
                </select>
              </div>
            </div>
          </div>
          {taxMode === 'custom' && (
            <div className="input-group">
              <label>Custom Tax Label</label>
              <div className="input-with-icon">
                <input value={customTaxLabel} onChange={(e) => setCustomTaxLabel(e.target.value)} />
              </div>
            </div>
          )}
          {taxMode === 'gst' && (
            <div className="input-group">
              <label>
                <input type="checkbox" checked={charges.intraState} onChange={(e) => updateCharges('intraState', e.target.checked)} />{' '}
                Intra-state supply (CGST + SGST split). Uncheck for inter-state (IGST).
              </label>
            </div>
          )}

          <LogoUploader logo={logo} setLogo={setLogo} />

          {/* Business */}
          <h3>From (Your Business)</h3>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 2 }}><label>Business Name</label><div className="input-with-icon"><input value={business.name} onChange={(e) => updateBusiness('name', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 2 }}><label>Tagline</label><div className="input-with-icon"><input value={business.tagline} onChange={(e) => updateBusiness('tagline', e.target.value)} /></div></div>
          </div>
          <div className="input-group"><label>Address</label><div className="input-with-icon"><input value={business.address} onChange={(e) => updateBusiness('address', e.target.value)} /></div></div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Phone</label><div className="input-with-icon"><input value={business.phone} onChange={(e) => updateBusiness('phone', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Email</label><div className="input-with-icon"><input value={business.email} onChange={(e) => updateBusiness('email', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Website</label><div className="input-with-icon"><input value={business.website} onChange={(e) => updateBusiness('website', e.target.value)} /></div></div>
          </div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>GSTIN / Tax ID</label><div className="input-with-icon"><input value={business.gstin} onChange={(e) => updateBusiness('gstin', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>PAN</label><div className="input-with-icon"><input value={business.pan} onChange={(e) => updateBusiness('pan', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Reg No.</label><div className="input-with-icon"><input value={business.regNo} onChange={(e) => updateBusiness('regNo', e.target.value)} /></div></div>
          </div>

          {/* Client */}
          <h3>Bill To (Customer)</h3>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Customer Name</label><div className="input-with-icon"><input value={client.name} onChange={(e) => updateClient('name', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Contact Person</label><div className="input-with-icon"><input value={client.contactPerson} onChange={(e) => updateClient('contactPerson', e.target.value)} /></div></div>
          </div>
          <div className="input-group"><label>Address</label><div className="input-with-icon"><input value={client.address} onChange={(e) => updateClient('address', e.target.value)} /></div></div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Phone</label><div className="input-with-icon"><input value={client.phone} onChange={(e) => updateClient('phone', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Email</label><div className="input-with-icon"><input value={client.email} onChange={(e) => updateClient('email', e.target.value)} /></div></div>
          </div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Customer GSTIN</label><div className="input-with-icon"><input value={client.gstin} onChange={(e) => updateClient('gstin', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Place of Supply</label><div className="input-with-icon"><input value={client.placeOfSupply} onChange={(e) => updateClient('placeOfSupply', e.target.value)} /></div></div>
          </div>

          {/* Bill meta */}
          <h3>Bill Details</h3>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Bill No</label><div className="input-with-icon"><input value={meta.billNo} onChange={(e) => updateMeta('billNo', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Issue Date</label><div className="input-with-icon"><input value={meta.issueDate} onChange={(e) => updateMeta('issueDate', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Due Date</label><div className="input-with-icon"><input value={meta.dueDate} onChange={(e) => updateMeta('dueDate', e.target.value)} /></div></div>
          </div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>PO / Reference</label><div className="input-with-icon"><input value={meta.poRef} onChange={(e) => updateMeta('poRef', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Payment Mode</label>
              <div className="input-with-icon">
                <select value={meta.paymentMode} onChange={(e) => updateMeta('paymentMode', e.target.value)}>
                  {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Status</label>
              <div className="input-with-icon">
                <select value={meta.status} onChange={(e) => updateMeta('status', e.target.value)}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Items */}
          <h3>Line Items</h3>
          {items.map((it, idx) => (
            <div key={it.id} style={{ border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, marginBottom: 10 }}>
              <div className="flex-row" style={{ gap: 8, alignItems: 'flex-end' }}>
                <div className="input-group" style={{ flex: 3 }}>
                  <label>Item #{idx + 1} Description</label>
                  <div className="input-with-icon"><input value={it.description} onChange={(e) => updateItem(it.id, 'description', e.target.value)} /></div>
                </div>
                {(taxMode === 'gst' || taxMode === 'igst') && (
                  <div className="input-group" style={{ flex: 1 }}>
                    <label>HSN/SAC</label>
                    <div className="input-with-icon"><input value={it.hsn} onChange={(e) => updateItem(it.id, 'hsn', e.target.value)} /></div>
                  </div>
                )}
                <button type="button" className="btn btn-outline" onClick={() => removeItem(it.id)} style={{ marginBottom: 16 }}><FaTrash /></button>
              </div>
              <div className="flex-row" style={{ gap: 8 }}>
                <div className="input-group" style={{ flex: 1 }}><label>Qty</label><div className="input-with-icon"><input type="number" value={it.qty} onChange={(e) => updateItem(it.id, 'qty', e.target.value)} /></div></div>
                <div className="input-group" style={{ flex: 1 }}><label>Unit</label><div className="input-with-icon"><input value={it.unit} onChange={(e) => updateItem(it.id, 'unit', e.target.value)} /></div></div>
                <div className="input-group" style={{ flex: 1 }}><label>Rate ({currSym})</label><div className="input-with-icon"><input type="number" value={it.rate} onChange={(e) => updateItem(it.id, 'rate', e.target.value)} /></div></div>
                <div className="input-group" style={{ flex: 1 }}><label>Disc ({currSym})</label><div className="input-with-icon"><input type="number" value={it.discount} onChange={(e) => updateItem(it.id, 'discount', e.target.value)} /></div></div>
                {taxMode !== 'none' && (
                  <div className="input-group" style={{ flex: 1 }}><label>Tax %</label><div className="input-with-icon"><input type="number" value={it.tax} onChange={(e) => updateItem(it.id, 'tax', e.target.value)} /></div></div>
                )}
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-outline" onClick={addItem}><FaPlus /> Add Line Item</button>

          {/* Charges */}
          <h3>Additional Charges & Adjustments</h3>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Shipping</label><div className="input-with-icon"><input type="number" value={charges.shipping} onChange={(e) => updateCharges('shipping', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Packing</label><div className="input-with-icon"><input type="number" value={charges.packing} onChange={(e) => updateCharges('packing', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Handling</label><div className="input-with-icon"><input type="number" value={charges.handling} onChange={(e) => updateCharges('handling', e.target.value)} /></div></div>
          </div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Overall Discount %</label><div className="input-with-icon"><input type="number" value={charges.discountPercent} onChange={(e) => updateCharges('discountPercent', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>Advance Paid</label><div className="input-with-icon"><input type="number" value={charges.advancePaid} onChange={(e) => updateCharges('advancePaid', e.target.value)} /></div></div>
          </div>
          <div className="input-group">
            <label>
              <input type="checkbox" checked={charges.roundOff} onChange={(e) => updateCharges('roundOff', e.target.checked)} />{' '}
              Round off total to nearest unit
            </label>
          </div>

          {/* Bank */}
          <h3>Payment Details (Optional)</h3>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>A/C Name</label><div className="input-with-icon"><input value={bank.accountName} onChange={(e) => updateBank('accountName', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>A/C No</label><div className="input-with-icon"><input value={bank.accountNo} onChange={(e) => updateBank('accountNo', e.target.value)} /></div></div>
          </div>
          <div className="flex-row" style={{ gap: 8 }}>
            <div className="input-group" style={{ flex: 1 }}><label>Bank</label><div className="input-with-icon"><input value={bank.bank} onChange={(e) => updateBank('bank', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>IFSC</label><div className="input-with-icon"><input value={bank.ifsc} onChange={(e) => updateBank('ifsc', e.target.value)} /></div></div>
            <div className="input-group" style={{ flex: 1 }}><label>UPI</label><div className="input-with-icon"><input value={bank.upi} onChange={(e) => updateBank('upi', e.target.value)} /></div></div>
          </div>

          <div className="input-group">
            <label>Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
          <div className="input-group">
            <label>Terms & Conditions</label>
            <textarea value={terms} onChange={(e) => setTerms(e.target.value)} rows={3} />
          </div>

          <div className="button-group">
            <button type="button" className="btn btn-primary" onClick={handleDownloadPDF}><FaDownload /> Download PDF</button>
            <button type="button" className="btn btn-outline" onClick={handlePrint}><FaPrint /> Print</button>
            <button type="button" className="btn btn-outline" onClick={handleReset}><FaRedo /> Reset</button>
          </div>
        </div>

        <div className="calculator-results">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {typeConfig && <typeConfig.Icon />} {headerTitle} Preview
          </h3>
          <Preview />
          <div style={{ marginTop: 12, textAlign: 'center', color: '#666', fontSize: 12 }}>
            Amount Due: {currSym} {formatNum(finalAmount)} · Tax: {currSym} {formatNum(computed.totalTax)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralBillGenerator;
